import React from 'react';
import { Box, SpaceBetween, StatusIndicator } from '@amzn/awsui-components-react/polaris';
import { ColumnDef, SegmentAggregates } from 'src/models/segment-cdt/SegmentCdtTypes';
import {
    buildReconciliationEntries,
    isGlBackedColumn,
    ReconciliationStatus,
    ReconciliationVerdict,
    reconciliationVerdict,
} from 'src/components/segment-cdt/detail/segmentReconciliation';

/** Shown in a figure cell that has no value. Wording, not punctuation, so it cannot be read as a number. */
const NO_FIGURE = 'Not pulled';

/**
 * A figure for display, grouped per the runtime locale (as the grid's own cells are). An absent figure stays
 * absent, reported in words rather than as `0` or as punctuation: on a reconciliation surface a `0` is a
 * factual claim that the balance is zero.
 */
function formatFigure(value?: number): string {
    return value == null ? NO_FIGURE : value.toLocaleString();
}

function headline(verdict: ReconciliationVerdict): { type: 'success' | 'warning' | 'info', text: string } {
    switch (verdict) {
        case ReconciliationVerdict.TIED:
            return { type: 'success', text: 'Every GL column ties to the segment rows' };
        case ReconciliationVerdict.UNTIED:
            return { type: 'warning', text: 'Some GL columns do not tie to the segment rows' };
        default:
            return { type: 'info', text: 'Nothing to reconcile yet' };
    }
}

function barClass(verdict: ReconciliationVerdict): string {
    switch (verdict) {
        case ReconciliationVerdict.TIED:
            return 'tied';
        case ReconciliationVerdict.UNTIED:
            return 'untied';
        default:
            return 'pending';
    }
}

/**
 * Per-column reconciliation of the GL pull against the segment rows, shown beneath the totals band.
 *
 * Answers the question the totals band raises but cannot settle: the period sums to X, but is that the whole
 * of what the GL pulled? One row per GL-backed column, with the GL aggregate, the segment sum and the gap in
 * aligned columns so a mismatch is visible without reading the figures.
 *
 * <p>The panel renders whenever the table has any GL-backed column, including when the server has returned
 * no unallocated amounts at all. An empty period is a real state to report ("no GL balance pulled yet"), and
 * hiding the panel instead would be indistinguishable from the feature not existing.
 *
 * <p>Only GL-backed columns appear - see `isGlBackedColumn`. A `MANUAL` or `DERIVED_PCT` column has no GL
 * balance to reconcile against, so listing it would imply a check that cannot be performed.
 */
export default function SegmentReconciliationBar(props: {
    aggregates?: SegmentAggregates;
    columns: ColumnDef[];
}) {
    const { aggregates, columns } = props;
    const entries = buildReconciliationEntries(aggregates, columns);

    // Nothing is known about the column set yet (the definition is still loading), so there is not even a
    // shape to draw. Distinct from "loaded, and no column is GL-backed", which is reported below.
    if (!(columns ?? []).length) {
        return null;
    }

    // The table genuinely has nothing to reconcile: every column is MANUAL or DERIVED_PCT. Say so, rather
    // than rendering an empty table or vanishing.
    if (!entries.length) {
        const hasGlBacked = (columns ?? []).some(isGlBackedColumn);
        return (
            <div className="segmentReconciliationBar pending">
                <SpaceBetween size="xs">
                    <Box variant="strong">Reconciliation</Box>
                    <div className="segmentReconciliationNote">
                        {hasGlBacked
                            ? 'No GL balance pull has populated this period yet.'
                            : 'This table has no GL balance pull columns, so there is nothing to reconcile.'}
                    </div>
                </SpaceBetween>
            </div>
        );
    }

    const verdict = reconciliationVerdict(entries);
    const status = headline(verdict);
    // Only worth saying when some columns did reconcile and others did not; if none did, the per-row
    // wording already carries it.
    const pendingCount = entries.filter((e) => e.status === ReconciliationStatus.NOT_PULLED).length;
    const showPendingNote = pendingCount > 0 && pendingCount < entries.length;

    return (
        <div className={`segmentReconciliationBar ${barClass(verdict)}`}>
            <SpaceBetween size="xs">
                <Box variant="strong">
                    Reconciliation{' '}
                    <StatusIndicator type={status.type}>{status.text}</StatusIndicator>
                </Box>
                <table className="segmentReconciliationTable">
                    <thead>
                        <tr>
                            <th scope="col">Column</th>
                            <th scope="col" className="reconNumeric">GL Aggregate</th>
                            <th scope="col" className="reconNumeric">Segment Sum</th>
                            <th scope="col" className="reconNumeric">Unallocated</th>
                            <th scope="col">Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {entries.map((e) => {
                            const untied = e.status === ReconciliationStatus.UNTIED;
                            const absent = e.status === ReconciliationStatus.NOT_PULLED;
                            return (
                                <tr key={e.columnId}>
                                    <td className="reconColumnName">{e.label}</td>
                                    <td className={`reconNumeric${e.glAggregate == null ? ' reconAbsent' : ''}`}>
                                        {formatFigure(e.glAggregate)}
                                    </td>
                                    <td className={`reconNumeric${e.segmentSum == null ? ' reconAbsent' : ''}`}>
                                        {formatFigure(e.segmentSum)}
                                    </td>
                                    <td className={`reconNumeric${untied ? ' reconGap' : ''}${absent ? ' reconAbsent' : ''}`}>
                                        {formatFigure(e.unallocated)}
                                    </td>
                                    <td>
                                        {absent
                                            ? <span className="reconAbsent">No GL balance pulled</span>
                                            : (
                                                <StatusIndicator type={untied ? 'warning' : 'success'}>
                                                    {untied ? 'Not tied' : 'Tied'}
                                                </StatusIndicator>
                                            )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                {showPendingNote && (
                    <div className="segmentReconciliationNote">
                        {`${pendingCount} of ${entries.length} GL columns have no balance pulled for this period yet.`}
                    </div>
                )}
            </SpaceBetween>
        </div>
    );
}
